import styled from 'styled-components';
import { useRef } from 'react';
import Image from 'next/legacy/image';
import useLockBodyScroll from 'hooks/useLockBodyScroll';
import useWindowEvent from 'hooks/useWindowEvent';
import NoSSR from './../utils/NoSSR';
import Portal from './../utils/Portal';

export type ImageModalProps = {
  open: boolean;
  src: string;
  alt?: string;
  width: number;
  height: number;
  onClose: () => void;
};

export const ImageModal: React.FC<ImageModalProps> = ({ src, alt, width, height, onClose, open }) => {
  useLockBodyScroll();
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: any) => {
    if (
      ref &&
      ref.current &&
      !ref.current.contains(event.target) &&
      event.target !== ref.current.parentNode?.firstChild
    ) {
      onClose();
    }
  };

  const handleKeyDown = (e: { keyCode: number }) => {
    if (e.keyCode === 27) {
      onClose();
    }
  };

  useWindowEvent('mousedown', handleClickOutside);
  useWindowEvent('keydown', handleKeyDown);

  return (
    // @ts-ignore
    <NoSSR>
      <Portal id={alt} visible={open}>
        <StyledImageModal>
          <div className="background" />
          <button className="close" aria-label="Close" onClick={onClose} />
          <div className="content">
            <div className="image" ref={ref}>
              <Image
                src={`${src.startsWith('//') ? `https:${src}` : src}`}
                alt={alt || ''}
                width={width}
                height={height}
                quality={100}
              />
            </div>
          </div>
        </StyledImageModal>
      </Portal>
    </NoSSR>
  );
};

export const StyledImageModal = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
  position: fixed;
  z-index: 1050;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;

  .background {
    background-color: rgba(0, 0, 0, 0.5);
    outline-color: var(--text);
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  .content {
    margin: 0 auto;
    position: relative;
    max-width: calc(100vw - 40px);
    width: auto;
    overflow-y: auto;
  }

  .image {
    position: relative;
    max-height: 80vh;
    max-width: 80vw;
    overflow: hidden;

    img {
      max-height: 80vh;
      max-width: 80vw;
    }

    > span {
      box-shadow:
        0 -2px 2px rgba(0, 0, 0, 0.05),
        0 1px 1px rgba(0, 0, 0, 0.05),
        0 2px 2px rgba(0, 0, 0, 0.05),
        0 4px 4px rgba(0, 0, 0, 0.05),
        0 8px 8px rgba(0, 0, 0, 0.05);
    }
  }

  .close {
    user-select: none;
    position: absolute;
    right: 0.5rem;
    top: 1rem;
    z-index: 1050;
    -webkit-appearance: none;
    background-color: transparent;
    border: none;
    border-radius: 290486px;
    color: inherit;
    cursor: pointer;
    display: inline-block;
    flex-grow: 0;
    flex-shrink: 0;
    font-size: 0;
    height: 20px;
    max-height: 20px;
    max-width: 20px;
    min-height: 20px;
    min-width: 20px;
    outline: none;
    vertical-align: top;
    width: 20px;

    &::before {
      background-color: currentColor;
      border: 1px solid transparent;
      content: '';
      display: block;
      left: 50%;
      position: absolute;
      top: 50%;
      width: 50%;
      transform: translateX(-50%) translateY(-50%) rotate(45deg);
      transform-origin: center center;
    }

    &::after {
      background-color: currentColor;
      border: 1px solid transparent;
      content: '';
      display: block;
      left: 50%;
      height: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%) rotate(45deg);
      transform-origin: center center;
    }
  }
`;

export default ImageModal;
